'use client';

import { useEffect } from 'react';
import { getCookie } from 'cookies-next';
import { useSetState } from 'minimal-shared/hooks';
import { getToken, getMessaging } from 'firebase/messaging';

import { useRouter } from 'src/routes/hooks';
import { paths, newSearchParams } from 'src/routes/paths';

import { CONFIG } from 'src/global-config';
import {
  getData,
  updateData,
  firebaseApp,
  onIdTokenChanged,
  DEFAULT_NOTIFICATIONS,
} from 'src/lib/firebase';

import { SETTINGS_STORAGE_KEY } from 'src/components/settings';

import { setSession } from './action';
import { AuthContext } from '../auth-context';

// ----------------------------------------------------------------------

const installSWorkers = () => {
  if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
    navigator.serviceWorker.register('/firebase-messaging-sw.js');
  }
};

export function AuthProvider({ children, userServerAuth }) {
  const router = useRouter();

  const { state, setState } = useSetState({ tokenNotification: null });

  const startNotifications = async () => {
    try {
      if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          throw new Error('Notifications permission denied');
        }
        await getToken(getMessaging(firebaseApp), { vapidKey: CONFIG.messaging.vapidKey })
          .then(async (currentToken) => {
            if (!currentToken) {
              throw new Error(
                'No registration token available. Request permission to generate one'
              );
            }
            setState({ tokenNotification: currentToken });
            if (userServerAuth?.uid && !userServerAuth?.notifications?.length) {
              await updateData(`users/${userServerAuth?.uid}`, {
                notifications: DEFAULT_NOTIFICATIONS,
              });
            }
          })
          .catch((error) => {
            throw error;
          });
      }
    } catch (error) {
      console.error('An error occurred while retrieving token:', error);
    }
  };

  const saveToken = async () => {
    const tokens = await getData({
      path: 'notificationsTokens',
      options: {
        where: [
          { field: 'uid', operator: '==', value: userServerAuth?.uid },
          { field: 'token', operator: '==', value: state.tokenNotification },
        ],
      },
    });

    if (!tokens.length) {
      updateData('notificationsTokens', {
        uid: userServerAuth?.uid,
        email: userServerAuth?.email,
        token: state.tokenNotification,
        allowed: userServerAuth?.notifications ?? DEFAULT_NOTIFICATIONS,
      });
    }
  };

  useEffect(
    () =>
      onIdTokenChanged(async (user) => {
        await setSession(user);
        if ((user?.uid && !userServerAuth?.uid) || (!user?.uid && userServerAuth?.uid)) {
          router.refresh();
        } else if (user?.uid && userServerAuth?.uid && !userServerAuth?.about) {
          const { countryName } = JSON.parse(await getCookie(SETTINGS_STORAGE_KEY));
          router.replace(`${paths.user.account}${newSearchParams({ country: countryName })}`);
        }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    installSWorkers();
    startNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (userServerAuth?.uid && state.tokenNotification) saveToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userServerAuth, state.tokenNotification]);

  const contextValues = {
    user: userServerAuth,
    authenticated: userServerAuth?.uid && !userServerAuth?.isAnonymous,
  };

  return <AuthContext value={contextValues}>{children}</AuthContext>;
}
